import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FileSignature, Shield, CheckCircle2, AlertTriangle, Download, Lock } from 'lucide-react';
import SignatureCanvas from '../components/SignatureCanvas';
import { SECTIONS } from '../lib/sectionTheme';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const A = SECTIONS.contracts;

const LOGO = "/brand/sp-logo.png";

export default function SignContractPage() {
  const { token } = useParams();
  const [c, setC] = useState(null);
  const [error, setError] = useState(null);
  const [sigData, setSigData] = useState('');
  const [sigTyped, setSigTyped] = useState('');
  const [ack, setAck] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    axios.get(`${API}/contracts/sign/${token}`)
      .then(r => setC(r.data))
      .catch(e => setError(e.response?.data?.detail || 'This signing link is invalid or has expired'));
  }, [token]);

  const sign = async () => {
    if (!ack) { alert('Please acknowledge the contract terms first'); return; }
    if (!sigData && !sigTyped.trim()) { alert('Please draw or type your signature'); return; }
    setSigning(true);
    try {
      await axios.post(`${API}/contracts/sign/${token}`, {
        signature_data: sigData, signature_typed: sigTyped.trim(), acknowledged: ack,
      });
      setSigned(true);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to sign. Please refresh and try again.');
    }
    setSigning(false);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md text-center bg-card border border-red-500/30 rounded-2xl p-8" data-testid="sign-error">
          <AlertTriangle className="w-12 h-12 mx-auto text-red-500 mb-4" />
          <h1 className="font-['Outfit'] text-xl font-bold text-foreground">Link unavailable</h1>
          <p className="text-sm text-muted-foreground mt-2">{error}</p>
          <p className="text-xs text-muted-foreground mt-4">Please contact SP Radiology if you believe this is in error.</p>
        </div>
      </div>
    );
  }

  if (!c) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><p className="text-muted-foreground text-sm">Loading contract…</p></div>;
  }

  const alreadySigned = signed || c.status === 'signed_by_employee' || c.status === 'countersigned';
  const isCountersigned = c.status === 'countersigned';
  const pdfUrl = `${API}/contracts/${c.contract_id}/pdf?token=${token}`;

  return (
    <div className="min-h-screen bg-background" data-testid="sign-page">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="SP Radiology Assistant" className="h-10 w-auto object-contain" onError={(e) => e.target.style.display='none'} />
            <div>
              <p className="font-['Outfit'] text-sm font-bold" style={{ color: '#0E2D5C' }}>SP RADIOLOGY</p>
              <p className="text-[10px] text-muted-foreground tracking-wider uppercase">Secure Contract Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Shield className="w-3 h-3" style={{ color: A.color }} />
            <span>AES-256 encrypted · TLS secured</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {alreadySigned ? (
          <div className="text-center bg-card border border-green-500/30 rounded-2xl p-10" data-testid="sign-success">
            <CheckCircle2 className="w-14 h-14 mx-auto text-green-500 mb-4" />
            <h1 className="font-['Outfit'] text-2xl font-bold text-foreground">
              {isCountersigned ? 'Contract fully executed' : 'Thank you, ' + c.employee_name}
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              {isCountersigned
                ? `This ${c.contract_type === 'employment' ? 'employment agreement' : 'job description'} has been signed by both parties. You can download a copy of the fully-executed PDF below.`
                : 'Your signature has been recorded. A copy will be emailed to you once it has been counter-signed by SP Radiology management.'}
            </p>
            {c.signed_at && (
              <p className="text-xs text-muted-foreground mt-3">
                Signed {new Date(c.signed_at).toLocaleString()}
                {c.countersigned_at && ` · Counter-signed ${new Date(c.countersigned_at).toLocaleString()}`}
              </p>
            )}
            {isCountersigned && (
              <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="download-signed-pdf"
                 className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-lg text-sm font-semibold text-white"
                 style={{ backgroundColor: A.color }}>
                <Download className="w-4 h-4" /> Download signed PDF
              </a>
            )}
            <div className="inline-flex items-center gap-2 mt-6 px-4 py-2 rounded-full text-xs text-muted-foreground bg-muted ml-2">
              <Lock className="w-3.5 h-3.5" /> Signed securely · encrypted at rest
            </div>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] mb-3" style={{ backgroundColor: A.colorBg, color: A.color }}>
                <FileSignature className="w-3 h-3" /> Contract to sign
              </div>
              <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-foreground" data-testid="sign-title">
                {c.contract_type === 'employment' ? 'Employment Agreement' : 'Job Description & Role Acknowledgement'}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">Please review the details below and sign to accept.</p>
            </div>

            {/* Contract body */}
            <div className="bg-card border border-border rounded-2xl p-6 mb-6 space-y-5" data-testid="sign-contract-body">
              <Section title="Parties & Role">
                <Row k="Employer" v="SP Radiology — Sanctuary Point Ultrasound & Radiology" />
                <Row k="Employee" v={c.employee_name} />
                <Row k="Email" v={c.employee_email} />
                <Row k="Job title" v={c.job_title} bold />
                {c.reports_to && <Row k="Reports to" v={c.reports_to} />}
                <Row k="Contract date" v={c.contract_date ? new Date(c.contract_date).toLocaleDateString() : '—'} />
              </Section>

              {c.contract_type === 'employment' && (
                <Section title="Terms of Employment">
                  <Row k="Employment type" v={c.employment_type} />
                  {c.start_date && <Row k="Start date" v={new Date(c.start_date).toLocaleDateString()} />}
                  <Row k="Hours per week" v={c.hours_per_week} />
                  <Row k="Probation" v={c.probation_period} />
                  <Row k="Notice period" v={c.notice_period} />
                  {c.work_location && <Row k="Location" v={c.work_location} />}
                  {c.salary_amount && <Row k="Remuneration" v={c.salary_amount} />}
                </Section>
              )}

              {c.purpose && <Section title="Purpose of Role"><p className="text-sm text-foreground">{c.purpose}</p></Section>}

              {c.duties && (
                <Section title="Main Duties & Responsibilities">
                  <ul className="text-sm text-foreground space-y-1 list-disc pl-5">
                    {c.duties.split('\n').filter(Boolean).map((d, i) => <li key={i}>{d}</li>)}
                  </ul>
                  <p className="text-xs italic text-muted-foreground mt-3 bg-muted/60 px-3 py-2 rounded border-l-2" style={{ borderLeftColor: A.color }}>
                    The above list is not exhaustive and the role may change to meet the overall objectives of the company.
                  </p>
                </Section>
              )}

              {(c.qualifications || c.experience || c.knowledge || c.skills || c.attributes) && (
                <Section title="Person Specification">
                  {c.qualifications && <SubField k="Qualifications" v={c.qualifications} />}
                  {c.experience && <SubField k="Experience" v={c.experience} />}
                  {c.knowledge && <SubField k="Knowledge" v={c.knowledge} />}
                  {c.skills && <SubField k="Skills" list={c.skills} />}
                  {c.attributes && <SubField k="Personal attributes" list={c.attributes} />}
                </Section>
              )}

              {c.contract_type === 'employment' && (
                <div className="text-xs text-foreground/80 bg-muted/40 rounded-lg p-4 leading-relaxed">
                  <p className="font-semibold mb-2">Confidentiality & Obligations</p>
                  <p>The Employee agrees to keep confidential all patient information, commercial terms, clinical protocols and internal business matters of SP Radiology both during and after the term of this employment, in accordance with the <i>Privacy Act 1988</i> (Cth) and applicable Australian privacy principles. The Employee will comply with all workplace health and safety obligations, radiation safety protocols and professional codes of conduct applicable to the role.</p>
                </div>
              )}
            </div>

            {/* Acknowledgement + signature */}
            <div className="bg-card border-2 rounded-2xl p-6" style={{ borderColor: A.ring }}>
              <h2 className="font-['Outfit'] text-lg font-bold text-foreground mb-1">Acknowledge & sign</h2>
              <p className="text-xs text-muted-foreground mb-5">By signing below you confirm that you have read, understood and accept the duties, responsibilities and obligations of your position as described above.</p>

              <label className="flex items-start gap-3 p-3 rounded-lg border border-border bg-muted/40 cursor-pointer mb-4">
                <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} data-testid="ack-checkbox"
                  className="mt-0.5 w-4 h-4 rounded border-border" style={{ accentColor: A.color }} />
                <span className="text-sm text-foreground">I have read, understood and accept the duties, responsibilities and obligations described in this {c.contract_type === 'employment' ? 'employment agreement' : 'job description'}.</span>
              </label>

              <div className="mb-4">
                <label className="block text-xs font-medium text-muted-foreground mb-2">Draw your signature</label>
                <SignatureCanvas onChange={setSigData} testId="employee-sig-canvas" />
              </div>

              <div className="mb-5">
                <label className="block text-xs font-medium text-muted-foreground mb-1">Or type your full name</label>
                <input type="text" value={sigTyped} onChange={e => setSigTyped(e.target.value)} placeholder={c.employee_name}
                  className="w-full max-w-sm px-3 py-2.5 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                  data-testid="employee-sig-typed" />
              </div>

              <button onClick={sign} disabled={signing || !ack} data-testid="sign-submit"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-white rounded-lg disabled:opacity-40" style={{ backgroundColor: A.color }}>
                <CheckCircle2 className="w-4 h-4" /> {signing ? 'Signing…' : 'Sign & submit'}
              </button>

              <p className="text-[11px] text-muted-foreground mt-4 flex items-center gap-1.5">
                <Shield className="w-3 h-3" /> Your signature is recorded along with the current date, time, and IP address to form a tamper-evident audit trail.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="font-['Outfit'] text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2 border-b border-border pb-1.5">{title}</h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ k, v, bold }) {
  return (
    <div className="grid grid-cols-3 text-sm gap-2">
      <span className="text-muted-foreground">{k}</span>
      <span className={`col-span-2 text-foreground ${bold ? 'font-semibold' : ''}`}>{v}</span>
    </div>
  );
}

function SubField({ k, v, list }) {
  return (
    <div className="mb-3">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{k}</p>
      {list ? (
        <ul className="text-sm text-foreground list-disc pl-5 space-y-0.5">
          {list.split('\n').filter(Boolean).map((l, i) => <li key={i}>{l}</li>)}
        </ul>
      ) : <p className="text-sm text-foreground">{v}</p>}
    </div>
  );
}
